import type { PostCreateSchema } from '@repo/schemas/post/postCreate';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { postPublicationTimestampSchema, type PostPublicationTimestampSchema } from '@repo/schemas/post/postPublicationTimestamp';
import { BookIcon, CheckCircle2Icon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { TimeInput, TimeInputItem } from '@/components/ui/input';
interface EditPostsPublishedAtDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publishedAt?: string;
  onSubmit: (data: Pick<PostCreateSchema, 'publishedAt'>) => void;
}

export default function EditPostsPublishedAtDialog(editPostsPublishedAtDialogProps: EditPostsPublishedAtDialogProps) {
  const { open, onOpenChange, publishedAt, onSubmit: _onSubmit } = editPostsPublishedAtDialogProps;

  const parseFormValues = (publishedAt: string) => {
    const date = new Date(publishedAt);
    if (Number.isNaN(date.getTime())) {
      return {
        date: '',
        time: null,
      };
    }

    const isoDateTime = date.toISOString(); // YYYY-MM-DDTHH:mm:ss.sssZ
    const isoTime = isoDateTime.slice(11, 16); // HH:mm (valid z.iso.time)
    const result = postPublicationTimestampSchema.safeParse({
      date: isoDateTime,
      time: isoTime,
    });

    if (!result.success) {
      return {
        date: '',
        time: null,
      };
    }

    return {
      date: result.data.date,
      time: result.data.time,
    };
  };

  const form = useForm<PostPublicationTimestampSchema>({
    defaultValues: {
      date: undefined,
      time: null,
    },
    mode: 'onSubmit',
    resolver: standardSchemaResolver(postPublicationTimestampSchema),
    values: publishedAt ? parseFormValues(publishedAt) : undefined,
  });

  const onSubmit = (data: PostPublicationTimestampSchema) => {
    let isoString: string;

    // Parse the date to get local date components
    const dateObj = new Date(data.date);

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const localDateStr = `${year}-${month}-${day}`;

    if (!data.time) {
      isoString = `${localDateStr}T00:00:00.000Z`;
    } else {
      isoString = `${localDateStr}T${data.time}:00.000Z`;
    }

    _onSubmit({ publishedAt: isoString });
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
    form.reset();
  };

  useEffect(() => {
    if (open && publishedAt) {
      form.reset(parseFormValues(publishedAt));
    }
  }, [open, publishedAt]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:max-w-96'}>
        <Form {...form}>
          <DialogBody>
            <DialogHeader className={'border-border-primary dark:bg-card bg-accent border-b'}>
              <FeaturedIcon size={'lg'}>
                <BookIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Zaplanuj Post</DialogTitle>
                <DialogDescription className={'text-muted-foreground text-xs'}>Wybierz datę i godzinę od kiedy ten post ma być widoczny.</DialogDescription>
              </div>
            </DialogHeader>

            <form className={'flex grow flex-col gap-3 p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <FormField
                name={'date'}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'w-full'}>
                    <FormLabel>Data</FormLabel>
                    <FormControl>
                      <DatePicker
                        {...field}
                        onBlur={(e) => e.preventDefault()}
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => {
                          if (!date) {
                            field.onChange(undefined);
                            return;
                          }
                          field.onChange(date.toISOString());
                          field.onBlur();
                        }}
                        matcher={{ before: new Date() }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name={'time'}
                control={form.control}
                render={({ field }) => (
                  <FormItem className={'w-full'}>
                    <FormLabel>Godzina</FormLabel>
                    <FormControl className="w-full">
                      <TimeInput disabled={field.disabled} value={field.value ?? undefined} onChange={(value) => field.onChange(value ? value : null)}>
                        <TimeInputItem picker="hours" onBlur={field.onBlur} />
                        <TimeInputItem picker="minutes" onBlur={field.onBlur} ref={field.ref} />
                      </TimeInput>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <CheckCircle2Icon />
              Zapisz
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
